import request, { ResponseError } from '@capturi/request'
import {
  type UseMutationResult,
  UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
type SftpUser = {
  id: string
  organizationUid: string
  username: string
  sftpUserId: null | string
  outOfSync: boolean
  outOfSyncReason: null | string
  sftpCreatedAt: null | Date
  sftpLastLoginAt: null | Date
}

export const useSftpUsers = <T = SftpUser[]>(
  select?: ((data: SftpUser[]) => T) | undefined,
): UseQueryResult<T, ResponseError> =>
  useQuery({
    queryKey: ['superpowers', 'sftpUsers'],
    queryFn: () => request.get<SftpUser[]>('superpowers/manage-sftp/list'),
    select,
  })

const byOrgUidSelector = (organizationUid: string) => (data: SftpUser[]) =>
  data.filter((d) => d.organizationUid === organizationUid)

export const useOrganizationSftpUsers = (
  organizationUid: string,
): UseQueryResult<SftpUser[], ResponseError> =>
  useSftpUsers(byOrgUidSelector(organizationUid))

export const useCreateSftpUser = (
  organizationUid: string,
): UseMutationResult<
  { username: string; password: string },
  ResponseError,
  { username: string }
> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload) =>
      request.post<{ username: string; password: string }>(
        `superpowers/manage-sftp/${organizationUid}`,
        {
          json: payload,
        },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['superpowers', 'sftpUsers'],
      })
    },
  })
}

export const useViewSftpUsers = (
  { organizationUid, userUid }: { organizationUid: string; userUid: string },
  { enabled }: { enabled: boolean },
): UseQueryResult<{ password: string }, ResponseError> =>
  useQuery({
    enabled: enabled,
    queryKey: [
      'superpowers',
      'sftpUsers',
      'password',
      organizationUid,
      userUid,
    ],
    queryFn: () =>
      request.get<{ password: string }>(
        `superpowers/manage-sftp/${organizationUid}/${userUid}/password`,
      ),
  })
